/**
 * Caja Menor Administrativo
 * Lista todos los comprobantes marcados como gasto administrativo (sin proyecto).
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import { UPLOAD_URL } from '../../config';

const fmt = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(Number(n || 0));
const fmtFecha = (d) => {
  if (!d) return '-';
  try { return new Date(d.length === 10 ? d + 'T12:00:00' : d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; }
};

const QUERY = `query($ingeniero_id: Int, $busqueda: String, $fecha_desde: String, $fecha_hasta: String) {
  cajaMenorAdministrativos(ingeniero_id: $ingeniero_id, busqueda: $busqueda, fecha_desde: $fecha_desde, fecha_hasta: $fecha_hasta) {
    items {
      id ingeniero_id ingeniero_nombre descripcion valor fecha estado
      valor_administrativo
      archivos { id nombre_archivo ruta_archivo tipo_archivo }
    }
    total cantidad
  }
}`;

const QUERY_RES = `{ residentes { id nombre } }`;

const CajaMenorAdministrativo = () => {
  const { error } = useNotification();
  const [items, setItems] = useState([]);
  const [resumen, setResumen] = useState({ total: 0, cantidad: 0 });
  const [residentes, setResidentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({ ingeniero_id: '', busqueda: '', fecha_desde: '', fecha_hasta: '' });

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const vars = {};
      if (filtros.ingeniero_id) vars.ingeniero_id = parseInt(filtros.ingeniero_id);
      if (filtros.busqueda) vars.busqueda = filtros.busqueda;
      if (filtros.fecha_desde) vars.fecha_desde = filtros.fecha_desde;
      if (filtros.fecha_hasta) vars.fecha_hasta = filtros.fecha_hasta;
      const r = await client.query(QUERY, vars);
      const d = r.cajaMenorAdministrativos;
      setItems(d.items || []);
      setResumen({ total: d.total || 0, cantidad: d.cantidad || 0 });
    } catch (e) {
      error('Error al cargar: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [filtros, error]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    client.query(QUERY_RES).then(r => setResidentes(r.residentes || [])).catch(() => {});
  }, []);

  const totalesPorResidente = useMemo(() => {
    const map = {};
    items.forEach(i => {
      const k = i.ingeniero_nombre || 'Sin asignar';
      if (!map[k]) map[k] = { nombre: k, total: 0, cantidad: 0 };
      map[k].total += i.valor;
      map[k].cantidad += 1;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [items]);

  return (
    <div style={{ padding: 20, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 14 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.4rem', color: '#1a1a1a' }}>Caja Menor — Administrativo</h1>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.85rem' }}>Comprobantes registrados como gasto administrativo (sin proyecto asociado)</p>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 18 }}>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 16px' }}>
          <div style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Cantidad</div>
          <div style={{ fontSize: '1.05rem', fontWeight: 700, marginTop: 4 }}>{resumen.cantidad}</div>
        </div>
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 16px' }}>
          <div style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Total Gastado Administrativo</div>
          <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#dc2626', marginTop: 4 }}>{fmt(resumen.total)}</div>
        </div>
        {totalesPorResidente.slice(0, 3).map(r => (
          <div key={r.nombre} style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 16px' }}>
            <div style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }} title={r.nombre}>{r.nombre.length > 22 ? r.nombre.slice(0, 22) + '…' : r.nombre}</div>
            <div style={{ fontSize: '1rem', fontWeight: 700, marginTop: 4 }}>{fmt(r.total)}</div>
            <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{r.cantidad} comp.</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <select value={filtros.ingeniero_id} onChange={(e) => setFiltros(f => ({ ...f, ingeniero_id: e.target.value }))}
          style={{ padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, minWidth: 220 }}>
          <option value="">Todos los residentes</option>
          {residentes.map(r => <option key={r.id} value={r.id}>{r.nombre}</option>)}
        </select>
        <input type="date" value={filtros.fecha_desde} onChange={(e) => setFiltros(f => ({ ...f, fecha_desde: e.target.value }))}
          style={{ padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8 }} />
        <input type="date" value={filtros.fecha_hasta} onChange={(e) => setFiltros(f => ({ ...f, fecha_hasta: e.target.value }))}
          style={{ padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8 }} />
        <input type="text" placeholder="Buscar por descripción o residente..." value={filtros.busqueda}
          onChange={(e) => setFiltros(f => ({ ...f, busqueda: e.target.value }))}
          style={{ padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, flex: 1, minWidth: 240 }} />
      </div>

      {/* Tabla */}
      {loading ? (
        <div style={{ padding: 60, textAlign: 'center', background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', color: '#64748b' }}>Cargando…</div>
      ) : items.length === 0 ? (
        <div style={{ padding: 60, textAlign: 'center', background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', color: '#94a3b8' }}>Sin comprobantes administrativos.</div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.87rem' }}>
            <thead>
              <tr style={{ background: '#1a2432', color: '#fff' }}>
                <th style={{ padding: '11px 10px', textAlign: 'left', fontSize: '0.76rem', textTransform: 'uppercase' }}>Fecha</th>
                <th style={{ padding: '11px 10px', textAlign: 'left', fontSize: '0.76rem', textTransform: 'uppercase' }}>Residente</th>
                <th style={{ padding: '11px 10px', textAlign: 'left', fontSize: '0.76rem', textTransform: 'uppercase' }}>Descripción</th>
                <th style={{ padding: '11px 10px', textAlign: 'right', fontSize: '0.76rem', textTransform: 'uppercase' }}>Valor</th>
                <th style={{ padding: '11px 10px', textAlign: 'center', fontSize: '0.76rem', textTransform: 'uppercase' }}>Estado</th>
                <th style={{ padding: '11px 10px', textAlign: 'left', fontSize: '0.76rem', textTransform: 'uppercase' }}>Archivos</th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: 10, whiteSpace: 'nowrap' }}>{fmtFecha(it.fecha)}</td>
                  <td style={{ padding: 10 }}>{it.ingeniero_nombre || '-'}</td>
                  <td style={{ padding: 10 }}>{it.descripcion}</td>
                  <td style={{ padding: 10, textAlign: 'right', fontWeight: 600, color: '#dc2626', fontVariantNumeric: 'tabular-nums' }}>{fmt(it.valor)}</td>
                  <td style={{ padding: 10, textAlign: 'center' }}>
                    <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 600,
                      background: it.estado === 'aprobado' ? '#dcfce7' : it.estado === 'rechazado' ? '#fee2e2' : '#fef3c7',
                      color: it.estado === 'aprobado' ? '#166534' : it.estado === 'rechazado' ? '#991b1b' : '#92400e' }}>
                      {it.estado || 'pendiente'}
                    </span>
                  </td>
                  <td style={{ padding: 10 }}>
                    {(it.archivos || []).length === 0 ? <span style={{ color: '#cbd5e1' }}>—</span> :
                      it.archivos.map(a => (
                        <a key={a.id} href={`${UPLOAD_URL}/caja-menor-file/${encodeURIComponent(a.ruta_archivo)}`} target="_blank" rel="noopener noreferrer"
                          style={{ display: 'inline-block', marginRight: 8, padding: '2px 8px', background: '#dbeafe', color: '#1e40af', borderRadius: 6, fontSize: '0.78rem', textDecoration: 'none' }}>
                          📎 {a.nombre_archivo}
                        </a>
                      ))}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="3" style={{ padding: 12, fontWeight: 700, background: '#f8fafc', borderTop: '2px solid #1a2432' }}>TOTAL</td>
                <td style={{ padding: 12, fontWeight: 700, textAlign: 'right', background: '#f8fafc', borderTop: '2px solid #1a2432', color: '#dc2626' }}>{fmt(resumen.total)}</td>
                <td colSpan="2" style={{ background: '#f8fafc', borderTop: '2px solid #1a2432' }}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export default CajaMenorAdministrativo;
